"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const React = __importStar(require("react"));
const react_code_blocks_1 = require("react-code-blocks");
const CgpEvaluator_1 = __importDefault(require("./CgpEvaluator"));
const CgpGenerator_1 = require("./CgpGenerator");
const CgpMutator_1 = __importDefault(require("./CgpMutator"));
exports.default = () => {
    const MIDDLE_NODES = 10;
    const POOL_SIZE = 10;
    const [pool, setPool] = React.useState((0, CgpGenerator_1.generateCgpPool)(POOL_SIZE, 4, MIDDLE_NODES, 3));
    const [fitnesses, setFitnesses] = React.useState(Array(POOL_SIZE).fill(0));
    function evaluateFitnesses(localPool) {
        const fitnesses = localPool.map((prog) => {
            return (0, CgpEvaluator_1.default)(prog, 4, MIDDLE_NODES, 3);
        });
        return fitnesses;
        //setFitnesses(fitnesses);
    }
    function mutatePool(localPool) {
        const newPool = localPool.map((prog) => {
            return (0, CgpMutator_1.default)(prog, 4, MIDDLE_NODES, 3);
        });
        return newPool;
        //setPool(newPool);
    }
    function runTournamentSelection(localPool, localFit) {
        const newPool = new Array();
        for (let i = 0; i < POOL_SIZE; i++) {
            const idxA = Math.floor(Math.random() * POOL_SIZE);
            const idxB = Math.floor(Math.random() * POOL_SIZE);
            if (localFit[idxA] > localFit[idxB]) {
                newPool.push(localPool[idxA]);
            }
            else {
                newPool.push(localPool[idxB]);
            }
        }
        return newPool;
    }
    return (React.createElement("div", null,
        React.createElement("p", null, "In the last post, we covered programs. Here, we're going to talk about pools, fitness, and evolution."),
        React.createElement("p", null, "The basic premise of genetic programming (and genetic algorithms) is this: Take a bunch of candidate solutions (the \"pool\"). Measure their fitness. Take the best ones (the \"fittest\") and use them to make the next set of solutions."),
        React.createElement("h2", null, "Fitness"),
        React.createElement("p", null,
            "The first step we have to tackle is how to measure fitness. This is where genetic algorithms shine, as you just need some way of measuring whether or not a solution is better,",
            React.createElement("i", null, " without necessarily knowing what the correct solution is. "),
            "As discussed in the last post, the problem we're going to tackle is a simple classification of the",
            " ",
            React.createElement("a", { href: "https://archive.ics.uci.edu/dataset/53/iris" }, "iris dataset"),
            "."),
        React.createElement("p", null,
            "As also discussed, we're going to take the traditional approach, similar to neural networks, by using 3 numerical outputs and utilizing a",
            " ",
            React.createElement("a", { href: "https://en.wikipedia.org/wiki/Softmax_function" }, "softmax function"),
            " ",
            "to normalize the output."),
        React.createElement("p", null, "This allows us to use cross entropy loss (technically softmax loss since we're talking multi-class)."),
        React.createElement("p", null,
            "However, there's an important distinction I want to make here. Cross entropy loss is a ",
            React.createElement("b", null, "loss"),
            " function (it's in the name). What we want is a ",
            React.createElement("b", null, "fitness"),
            " function. While it's pretty easy for a single system to do:"),
        React.createElement(react_code_blocks_1.CodeBlock, { text: "function getBetterIndividual(A:program, B:program, input){\nlossA = crossEntropyLoss(evaluate(A, input));\nlossB = crossEntropyLoss(evaluate(B, input));\nif lossA < lossB\n    return A;\nelse\n    return B;\n}" }),
        React.createElement("p", null, "I prefer keeping my functions using the same definition. For my personal preference, a fitness function should return a higher value for a \"fitter\" individual. As loss is higher = worse, we'll simply take the inverse:"),
        React.createElement(react_code_blocks_1.CodeBlock, { text: "function fitness(A:program, input){\nlossA = crossEntropyLoss(evaluate(A, input));\nreturn -1 * lossA;\n}" }),
        React.createElement("h2", null, "Pools"),
        React.createElement("p", null, "Now we can talk about pools. There's a couple of variations, but the easiest is to treat the pool as simply a group of programs:"),
        React.createElement(react_code_blocks_1.CodeBlock, { text: "var pool:Program[] = new Array(POOL_SIZE);" }),
        React.createElement("p", null, "And populate it using our generator from last post:"),
        React.createElement(react_code_blocks_1.CodeBlock, { text: "for(var i=0; i<POOL_SIZE; i++){\npool[i] = generateCgpProgram(NUM_IN, NUM_MID, NUM_OUT);\n}" }),
        React.createElement("p", null, "Then we can easily measure our pool's fitness:"),
        React.createElement(react_code_blocks_1.CodeBlock, { text: "var fitnesses:number[] = new Array(POOL_SIZE);\nfor(var i=0; i<POOL_SIZE; i++){\nfitnesses[i] = fitness(pool[i], inputs);\n}" }),
        React.createElement("h2", null, "Selection"),
        React.createElement("p", null, "And we come to the fun part, building the next generation, which means choosing which individuals to keep."),
        React.createElement("p", null, "There's a couple different approaches. Weighted selection, lexicase selection, etc. But to get things going and keep it simple, we're going to use tournament selection."),
        React.createElement("p", null, "In tournament selection, you take a subset of individuals, and compare their fitnesses. Highest one moves on. There's plenty of variations on how to do this (replacing individuals back in running, changing group sizes, changing how many move on, etc). For what we're doing, we're going to keep it simple with just 2, from all possible. Repeating until we have a full next generation:"),
        React.createElement(react_code_blocks_1.CodeBlock, { text: "var newPool:Program[] = new Array(POOL_SIZE);\nfor(var i=0; i<POOL_SIZE; i++){\nidxA = random() * POOL_SIZE;\nidxB = random() * POOL_SIZE;\nif (fitnesses[idxA] > fitnesses[idxB]){\n    newPool[i] = pool[idxA].clone();\n}\nelse {\n    newPool[i] = pool[idxB].clone();\n}\n}" }),
        React.createElement("h2", null, "Mutation"),
        React.createElement("p", null, "So, we have a new pool composed of the fittest individuals from the previous pool. While this is great, all it will accomplish is eventually converging on a pool full of copies of the fittest individual from our initial generation. To actually achieve evolution, we need to introduce variation, which is where mutation comes in."),
        React.createElement("p", null, "Just like everything else, there's more than one way to do this, and I'd like to play with each variation to see their merits. But for now, we'll settle on simple point mutation, which is simply taking a program, going element by element, and seeing if we should mutate said element."),
        React.createElement("p", null, "As we're using CGP, we'll be working at the node level. That is going node by node, and determining if that node should be mutated. If so, we'll do the \"mutation\" by simply generating a brand new node in it's place."),
        React.createElement("h2", null, "Testing"),
        React.createElement("p", null,
            "Now the moment of truth. While I've implemented genetic programs in various languages, this is the first time doing it in typescript",
            " ",
            React.createElement("b", null, "and"),
            " the first time trying to do it in a way that browsers can render. So, here we go:"),
        React.createElement(react_code_blocks_1.CodeBlock, { text: pool
                .map((prog, idx) => {
                return `Fitness: ${fitnesses[idx]}. Program: ${prog}`;
            })
                .join("\n") }),
        React.createElement("button", { type: "button", onClick: (e) => {
                const localPool = (0, CgpGenerator_1.generateCgpPool)(POOL_SIZE, 4, MIDDLE_NODES, 3);
                setPool(localPool);
                setFitnesses(evaluateFitnesses(localPool));
            } }, "Reset Pool"),
        React.createElement("button", { type: "button", onClick: (e) => {
                setFitnesses(evaluateFitnesses(pool));
            } }, "Evaluate Fitness"),
        React.createElement("button", { type: "button", onClick: (e) => {
                const newPool = runTournamentSelection(pool, fitnesses);
                const newFitnesses = evaluateFitnesses(newPool);
                setPool(newPool);
                setFitnesses(newFitnesses);
            } }, "Run tournament selection"),
        React.createElement("button", { type: "button", onClick: (e) => {
                const newPool = mutatePool(pool);
                const newFitnesses = evaluateFitnesses(newPool);
                setPool(newPool);
                setFitnesses(newFitnesses);
            } }, "Mutate"),
        React.createElement("button", { type: "button", onClick: (e) => {
                const newFitnesses = evaluateFitnesses(pool);
                const newPool1 = runTournamentSelection(pool, newFitnesses);
                const newPool2 = mutatePool(newPool1);
                const newFitnesses2 = evaluateFitnesses(newPool2);
                setPool(newPool2);
                setFitnesses(newFitnesses2);
            } }, "Run Generation (evaluate, select, mutate)")));
};
